import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const ADSENSE_CLIENT = "ca-pub-5354419908808807";
const ADSENSE_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;

let adsensePromise: Promise<void> | null = null;

/** Resolve fora da janela crítica de carregamento, sem remover o AdSense. */
function whenIdle(): Promise<void> {
  return new Promise((resolve) => {
    let timer = 0;
    let done = false;
    const interactionEvents = ["pointerdown", "keydown", "touchstart"] as const;
    const finish = () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      interactionEvents.forEach((event) => window.removeEventListener(event, finish));
      resolve();
    };
    const idle = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
        .requestIdleCallback;
      const defer = () => {
        if (ric) ric(finish, { timeout: 3000 });
        else finish();
      };
      // Os scripts de anúncios são o maior custo de avaliação apontado pelo
      // Lighthouse. Para visitantes reais, a primeira interação libera o
      // carregamento imediatamente; o fallback preserva a exibição automática.
      interactionEvents.forEach((event) =>
        window.addEventListener(event, defer, { once: true, passive: true }),
      );
      timer = window.setTimeout(defer, 20_000);
    };
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });
  });
}

/** Aguarda o script do AdSense (carregado no <head>) ficar disponível. */
function loadAdsense(): Promise<void> {
  if (!adsensePromise) {
    adsensePromise = whenIdle().then(
      () =>
        new Promise<void>((resolve, reject) => {
          const w = window as unknown as { adsbygoogle?: unknown };
          if (w.adsbygoogle) return resolve();
          // O script já está no <head> (async): aguarda seu carregamento
          // em vez de injetar uma segunda cópia.
          const existing = document.querySelector<HTMLScriptElement>(
            `script[src^="https://pagead2.googlesyndication.com"]`,
          );
          if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error("AdSense falhou ao carregar")), { once: true });
            return;
          }
          // Fallback: injeta se por algum motivo não estiver presente.
          const s = document.createElement("script");
          s.src = ADSENSE_SRC;
          s.async = true;
          s.crossOrigin = "anonymous";
          s.onload = () => resolve();
          s.onerror = () => reject(new Error("AdSense falhou ao carregar"));
          document.head.appendChild(s);
        }),
    );
  }
  return adsensePromise;
}


/**
 * Espaço de anúncio (Google AdSense) com carregamento adiado:
 * - No servidor e na 1ª renderização do cliente renderiza apenas um
 *   placeholder de altura fixa (zero CLS e sem divergência de hidratação).
 * - O script do AdSense (pesado) só é baixado quando o espaço entra na
 *   viewport — nunca bloqueia FCP/LCP.
 */
export function AdSlot({
  className,
  minHeight = 100,
  slot,
}: {
  className?: string;
  minHeight?: number;
  /** AdSense slot ID (opcional). Se omitido, usa formato responsivo automático. */
  slot?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Observa quando o espaço entra (ou está perto de entrar) na viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Só injeta o anúncio depois que o script carregou
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    loadAdsense()
      .then(() => {
        if (cancelled) return;
        try {
          // @ts-expect-error adsbygoogle é injetado pelo script do AdSense
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch {
          /* anúncio já preenchido ou bloqueado */
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visible]);

  return (
    <div
      ref={containerRef}
      className={cn("w-full", className)}
      style={{ minHeight }}
      aria-hidden={!visible}
    >
      {visible ? (
        <ins
          className="adsbygoogle"
          style={{ display: "block", minHeight, width: "100%" }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={slot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      ) : null}
    </div>
  );
}
